import { useEffect, useState, useCallback } from 'react';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { AdvertisementCampaign } from '@/types';

const AD_DURATION_MS = 3000;

interface AdvertisementPopupProps {
  communityId: string;
  enabled: boolean;
}

interface PopupCarouselProps {
  communityId: string;
  campaigns: AdvertisementCampaign[];
}

function AdSlideTimer({
  onComplete,
  children,
}: {
  onComplete: () => void;
  children: (progress: number) => React.ReactNode;
}) {
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / AD_DURATION_MS) * 100);
      setProgress(remaining);

      if (elapsed >= AD_DURATION_MS) {
        clearInterval(timer);
        onComplete();
      }
    }, 50);

    return () => clearInterval(timer);
  }, [onComplete]);

  return <>{children(progress)}</>;
}

function PopupCarousel({ communityId, campaigns }: PopupCarouselProps) {
  const [open, setOpen] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentCampaign = campaigns[currentIndex];
  const ad = currentCampaign.advertisement;

  const handleSlideComplete = useCallback(() => {
    if (currentIndex < campaigns.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else {
      setOpen(false);
    }
  }, [currentIndex, campaigns.length]);

  const handleSkip = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    axiosInstance
      .post(`/api/communities/${communityId}/advertisement-campaigns/${currentCampaign.id}/click`)
      .catch(console.error);
  }, [communityId, currentCampaign.id]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleSkip()}>
      <DialogContent
        hideCloseButton
        className="max-w-none w-screen h-[100dvh] max-h-screen rounded-none p-0 overflow-hidden border-none bg-background/95 backdrop-blur-xl flex flex-col"
      >
        <DialogTitle className="sr-only">{ad.title}</DialogTitle>

        <AdSlideTimer key={currentIndex} onComplete={handleSlideComplete}>
          {(progress) => (
            <>
              <div className="absolute top-0 left-0 right-0 z-10 h-1 bg-muted">
                <div
                  className="h-full bg-primary/50 transition-[width] duration-75 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>

              <div className="relative pt-1 flex-1 flex flex-col min-h-0">
                <a
                  href={ad.redirectUrl || '#'}
                  target={ad.redirectUrl ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="flex-1 flex flex-col min-h-0"
                  onPointerDown={handleClick}
                >
                  <picture className="w-full flex-1 flex flex-col min-h-0">
                    {currentCampaign.verticalMediaUrl && currentCampaign.horizontalMediaUrl ? (
                      <>
                        <source
                          media="(min-width: 640px)"
                          srcSet={currentCampaign.horizontalMediaUrl}
                        />
                        <source
                          media="(max-width: 639px)"
                          srcSet={currentCampaign.verticalMediaUrl}
                        />
                        <img
                          src={currentCampaign.verticalMediaUrl}
                          alt={ad.title}
                          className="w-full flex-1 object-contain min-h-0"
                        />
                      </>
                    ) : (
                      <img
                        src={
                          currentCampaign.verticalMediaUrl ||
                          currentCampaign.horizontalMediaUrl ||
                          ''
                        }
                        alt={ad.title}
                        className="w-full flex-1 object-contain min-h-0"
                      />
                    )}
                  </picture>
                </a>

                <div className="flex items-center justify-between px-6 pb-6 sm:px-8 sm:pb-8 shrink-0">
                  <span className="text-sm text-muted-foreground">
                    {campaigns.length > 1
                      ? `${currentIndex + 1} of ${campaigns.length}`
                      : 'Sponsored'}
                  </span>
                  <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
                    Skip
                  </Button>
                </div>
              </div>
            </>
          )}
        </AdSlideTimer>
      </DialogContent>
    </Dialog>
  );
}

export function AdvertisementPopup({ communityId, enabled }: AdvertisementPopupProps) {
  const { data } = useSWR(
    enabled
      ? `/api/communities/${communityId}/advertisement-campaigns?active=true&type=POPUP`
      : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );

  const campaigns: AdvertisementCampaign[] = data?.campaigns ?? [];

  if (campaigns.length === 0) return null;

  return <PopupCarousel key={communityId} communityId={communityId} campaigns={campaigns} />;
}
